import { useEffect, useRef, useState, useCallback } from 'react'
import { WS_BASE } from '../config/api'

export function useWebSocket(lobbyId, onMessage, userId = null, userName = null) {
  const wsRef = useRef(null)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(null)
  const onMessageRef = useRef(onMessage)

  // Keep the callback ref up to date
  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    if (!lobbyId) return

    // Use configured WebSocket base URL, or construct from current location
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = WS_BASE || `${wsProtocol}//${window.location.host}`
    
    // Add user info as query params (frontend uses local auth, not cookies)
    const params = new URLSearchParams()
    if (userId) params.set('userId', userId)
    if (userName) params.set('userName', userName)
    const queryString = params.toString()
    
    const wsUrl = `${wsHost}/api/lobbies/${lobbyId}/ws${queryString ? '?' + queryString : ''}`

    let ws = null
    
    try {
      ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        setError(null)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (onMessageRef.current) {
            onMessageRef.current(data)
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onclose = () => {
        setConnected(false)
      }

      ws.onerror = (err) => {
        setError('Connection failed')
        setConnected(false)
      }
    } catch (err) {
      setError('Failed to create connection')
    }

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [lobbyId, userId, userName])

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      return true
    }
    return false
  }, [])

  return { connected, error, sendMessage }
}
